/** Small, dependency-free text utilities shared by extraction, cleaning and the UI. */

/**
 * Character classes are built from code points so the source stays ASCII-only: literal
 * zero-width / bidi / combining characters are invisible in diffs and easy to corrupt.
 */
type CodePointPart = number | readonly [number, number];

function charClass(...parts: CodePointPart[]): string {
  return parts
    .map((part) =>
      typeof part === 'number'
        ? String.fromCodePoint(part)
        : `${String.fromCodePoint(part[0])}-${String.fromCodePoint(part[1])}`,
    )
    .join('');
}

// U+0610–U+061A, U+064B–U+065F, U+0670, U+06D6–U+06ED
const ARABIC_DIACRITICS = new RegExp(`[${charClass([0x610, 0x61a], [0x64b, 0x65f], 0x670, [0x6d6, 0x6ed])}]`, 'g');
// U+0640 tatweel (kashida)
const TATWEEL = new RegExp(charClass(0x640), 'g');
// U+0300–U+036F combining diacritical marks
const COMBINING_MARKS = new RegExp(`[${charClass([0x300, 0x36f])}]`, 'g');
// Zero-width and bidi control characters that `\s` does not cover.
const ZERO_WIDTH = new RegExp(`[${charClass([0x200b, 0x200f], [0x202a, 0x202e], [0x2060, 0x2064], 0xfeff)}]`, 'g');
// Arabic-Indic (U+0660–U+0669) and extended Arabic-Indic (U+06F0–U+06F9) digits
const ARABIC_DIGITS = new RegExp(`[${charClass([0x660, 0x669])}]`, 'g');
const PERSIAN_DIGITS = new RegExp(`[${charClass([0x6f0, 0x6f9])}]`, 'g');
// Alef variants -> plain alef, plus the other orthographic normalizations.
const ALEF_VARIANTS = new RegExp(`[${charClass(0x622, 0x623, 0x625, 0x671)}]`, 'g');
const ALEF = String.fromCodePoint(0x627);
const ALEF_MAKSURA = new RegExp(charClass(0x649), 'g');
const YEH = String.fromCodePoint(0x64a);
const TEH_MARBUTA = new RegExp(charClass(0x629), 'g');
const HEH = String.fromCodePoint(0x647);
const WAW_HAMZA = new RegExp(charClass(0x624), 'g');
const WAW = String.fromCodePoint(0x648);
const YEH_HAMZA = new RegExp(charClass(0x626), 'g');

const ELLIPSIS = String.fromCodePoint(0x2026);
const RIGHT_SINGLE_QUOTE = String.fromCodePoint(0x2019);

/** Collapse all whitespace into single spaces (`\s` already covers NBSP and U+FEFF). */
export function collapseWhitespace(value: string): string {
  return value.replace(ZERO_WIDTH, '').replace(/\s+/g, ' ').trim();
}

/** Convert Arabic-Indic and Persian digits to ASCII digits. */
export function toAsciiDigits(value: string): string {
  return value
    .replace(ARABIC_DIGITS, (digit) => String(digit.charCodeAt(0) - 0x0660))
    .replace(PERSIAN_DIGITS, (digit) => String(digit.charCodeAt(0) - 0x06f0));
}

/**
 * Normalize Arabic orthographic variants so equivalent spellings compare equal:
 * alef forms, alef maqsura, ta marbuta, hamza carriers, diacritics and tatweel.
 */
export function normalizeArabic(value: string): string {
  return value
    .replace(ARABIC_DIACRITICS, '')
    .replace(TATWEEL, '')
    .replace(ALEF_VARIANTS, ALEF)
    .replace(ALEF_MAKSURA, YEH)
    .replace(TEH_MARBUTA, HEH)
    .replace(WAW_HAMZA, WAW)
    .replace(YEH_HAMZA, YEH);
}

/** Remove Latin diacritics (é -> e) while leaving other scripts intact. */
export function stripLatinDiacritics(value: string): string {
  return value.normalize('NFKD').replace(COMBINING_MARKS, '').normalize('NFC');
}

/** Aggressive normalization for comparison keys (not for display). */
export function foldForComparison(value: string): string {
  return collapseWhitespace(normalizeArabic(stripLatinDiacritics(toAsciiDigits(value))).toLowerCase());
}

export function truncate(value: string, max: number): string {
  if (value.length <= max) return value;
  return `${value.slice(0, Math.max(0, max - 1)).trimEnd()}${ELLIPSIS}`;
}

export function isBlank(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  ndash: String.fromCodePoint(0x2013),
  mdash: String.fromCodePoint(0x2014),
  hellip: ELLIPSIS,
  copy: String.fromCodePoint(0x00a9),
  reg: String.fromCodePoint(0x00ae),
  trade: String.fromCodePoint(0x2122),
  laquo: String.fromCodePoint(0x00ab),
  raquo: String.fromCodePoint(0x00bb),
  rsquo: RIGHT_SINGLE_QUOTE,
  lsquo: String.fromCodePoint(0x2018),
  rdquo: String.fromCodePoint(0x201d),
  ldquo: String.fromCodePoint(0x201c),
};

/** Decode the HTML entities that commonly survive text extraction. */
export function decodeEntities(value: string): string {
  return value.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (match, entity: string) => {
    if (entity.startsWith('#x') || entity.startsWith('#X')) {
      const code = Number.parseInt(entity.slice(2), 16);
      return Number.isFinite(code) ? String.fromCodePoint(code) : match;
    }
    if (entity.startsWith('#')) {
      const code = Number.parseInt(entity.slice(1), 10);
      return Number.isFinite(code) ? String.fromCodePoint(code) : match;
    }
    return NAMED_ENTITIES[entity.toLowerCase()] ?? match;
  });
}

/** Strip tags from an HTML fragment and return readable text. */
export function htmlToText(html: string): string {
  const withoutBlocks = html
    .replace(/<(script|style|noscript|template|svg)[^>]*>[\s\S]*?<\/\1>/gi, ' ')
    .replace(/<!--[\s\S]*?-->/g, ' ')
    .replace(/<(br|\/p|\/div|\/li|\/h[1-6]|\/tr)[^>]*>/gi, '\n')
    .replace(/<[^>]+>/g, ' ');
  return decodeEntities(withoutBlocks)
    .split('\n')
    .map((line) => collapseWhitespace(line))
    .filter(Boolean)
    .join('\n');
}

/** Tokenize a string into lower-cased alphanumeric tokens (unicode aware). */
export function tokenize(value: string): string[] {
  return foldForComparison(value)
    .split(/[^\p{L}\p{N}]+/u)
    .filter((token) => token.length > 0);
}

const WORD_PATTERN = new RegExp(`\\p{L}[\\p{L}'${RIGHT_SINGLE_QUOTE}]*`, 'gu');

export function titleCase(value: string): string {
  return value.replace(WORD_PATTERN, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
}

export function slugify(value: string): string {
  return foldForComparison(value)
    .replace(/[^\p{L}\p{N}]+/gu, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 48);
}

export function uniqueBy<T>(items: readonly T[], key: (item: T) => string): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const item of items) {
    const k = key(item);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(item);
  }
  return out;
}
